import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  InputAdornment,
  Pagination,
  Paper,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import FlagIcon from "@mui/icons-material/Flag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import axiosInstance from "../../axiosInstance";
import { openModal } from "../../redux/actions/actions";
import CreateStudentModal from "../../components/Students/CreateStudentModal";
import BreadCrumbs from "../../components/Breadcrumbs";
import StyledTitleDot from "../../components/common/StyledTitleDot";
import NoDataFound from "../../components/NoDataFound";
import { formatStudentNoteHtml } from "../../utils/formatStudentNoteHtml";

const PAGE_SIZE = 10;
const PRIMARY = "#f08049";

const iconSx = { fontSize: 20, mr: 1, color: PRIMARY, top: "3px", position: "relative" };

function Field({ icon, label, value, minWidth = 140, maxWidth = 240, breakAll = false }) {
  return (
    <Box sx={{ minWidth, maxWidth, flex: "0 1 auto" }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          borderBottom: "1px solid #efefef",
          pb: 1,
          textTransform: "capitalize",
          display: "block",
        }}
      >
        {icon}
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 2,
          fontWeight: 400,
          wordBreak: breakAll ? "break-all" : "normal",
          overflowWrap: "anywhere",
          textTransform: breakAll ? "none" : "capitalize",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
}

export default function Students() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lightMode = useSelector((state) => state.mode.lightMode);

  const [students, setStudents] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const authHeaders = () => {
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("loginUser");
    return {
      Authorization: `Bearer ${accessToken}`,
      XRefreshToken: `${token}`,
      "Content-Type": "application/json",
    };
  };

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (search) params.append("search", search);
      const url = `${process.env.REACT_APP_BASE_URL}auth/students?${params.toString()}`;
      const res = await axiosInstance.get(url, { headers: authHeaders() });
      setStudents(res?.data?.results || res?.data?.student || []);
      setCount(res?.data?.count || 0);
    } catch (err) {
      console.error("Error loading students:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const fetchNotes = async (studentId) => {
    setNotesLoading(true);
    setNotes([]);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}auth/students/${studentId}/notes`;
      const res = await axiosInstance.get(url, { headers: authHeaders() });
      setNotes(res?.data?.notes || []);
    } catch (err) {
      console.error("Error loading notes:", err?.message);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleOpenCreate = () => {
    dispatch(
      openModal(
        <CreateStudentModal onCreated={() => { setPage(1); fetchStudents(); }} />,
        "createStudent"
      )
    );
  };

  const handleOpenDrawer = (student) => {
    setSelectedStudent(student);
    if (student?.student_id) fetchNotes(student.student_id);
  };

  const handleCloseDrawer = () => {
    setSelectedStudent(null);
    setNotes([]);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const pageTitle = t("students") || "Students";
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const formatName = (n) =>
    (n || "")
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0]?.toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  return (
    <>
      <Typography textTransform={"capitalize"} className="title" variant="h4">
        {pageTitle}
        <StyledTitleDot />
      </Typography>
      <BreadCrumbs routs={[` ${pageTitle}`]} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mt: 3,
          mb: 3,
        }}
      >
        <Box component="form" onSubmit={handleSearchSubmit}>
          <TextField
            size="small"
            placeholder={t("search") || "Search"}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ borderRadius: "1rem", textTransform: "capitalize" }}
        >
          {t("newStudent") || "New Student"}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : !students.length ? (
        <NoDataFound />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: ".25rem",
            }}
          >
            {students.map((row) => (
              <Paper
                key={row.student_id}
                elevation={0}
                onClick={() => handleOpenDrawer(row)}
                sx={{
                  cursor: "pointer",
                  boxShadow: `0 0 2px 0 ${
                    lightMode ? `rgba(145 158 171 / 0.2)` : "#00000040"
                  },0 12px 24px -4px ${
                    lightMode ? `rgba(145 158 171 / 0.12)` : "#00000040"
                  }`,
                  borderRadius: "1rem",
                  padding: "1rem",
                  backgroundColor: lightMode ? "#fff" : "#292929",
                  transition: "transform 120ms ease",
                  "&:hover": { transform: "translateY(-1px)" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    padding: ".5rem",
                    alignItems: "flex-start",
                  }}
                >
                  <Field
                    icon={<BadgeIcon sx={iconSx} />}
                    label={t("studentName") || "Name"}
                    value={formatName(row.student_name)}
                    minWidth={160}
                    maxWidth={220}
                  />
                  <Field
                    icon={<EmailIcon sx={iconSx} />}
                    label={t("emailAddress") || "Email"}
                    value={row.student_email}
                    minWidth={220}
                    maxWidth={280}
                    breakAll
                  />
                  <Field
                    icon={<LocalPhoneIcon sx={iconSx} />}
                    label={t("phone") || "Phone"}
                    value={row.student_phone}
                    minWidth={140}
                    maxWidth={180}
                  />
                  <Field
                    icon={<FlagIcon sx={iconSx} />}
                    label={t("nationality") || "Nationality"}
                    value={row.nationality_name}
                    minWidth={140}
                    maxWidth={180}
                  />
                  <Field
                    icon={<CalendarMonthIcon sx={iconSx} />}
                    label={t("createdAt") || "Created"}
                    value={row.created_at ? dayjs(row.created_at).format("YYYY-MM-DD") : "-"}
                    minWidth={120}
                    maxWidth={140}
                  />

                  <Box sx={{ ml: "auto", alignSelf: "center" }}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDrawer(row);
                      }}
                      variant="contained"
                      sx={{
                        textTransform: "capitalize",
                        borderRadius: "1rem",
                        backgroundColor: PRIMARY,
                        color: "#fff",
                        "&:hover": { backgroundColor: PRIMARY },
                      }}
                    >
                      {t("details") || "Details"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            variant="outlined"
            shape="rounded"
            sx={{ mt: 6, display: "flex", justifyContent: "center" }}
          />
        </>
      )}

      <Drawer
        anchor="right"
        open={Boolean(selectedStudent)}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, p: 3 } }}
      >
        {selectedStudent && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <Typography variant="h6" gutterBottom>
              {selectedStudent.student_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              {selectedStudent.student_email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedStudent.student_phone}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              {t("notes") || "Notes"}
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
            {notesLoading ? (
              <CircularProgress size={20} />
            ) : notes.length === 0 ? null : (
              notes.map((n) => (
                <Paper
                  key={n.note_id}
                  variant="outlined"
                  sx={{ p: 2, mb: 1.5, borderRadius: "1rem" }}
                >
                  {n.note_title ? (
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {n.note_title}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      "& .student-note-mention": { color: "#1976d2", fontWeight: 600 },
                    }}
                    dangerouslySetInnerHTML={{ __html: formatStudentNoteHtml(n.note_text) }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={dayjs(n.created_at).format("YYYY-MM-DD HH:mm")}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Paper>
              ))
            )}
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
}
